import { useEffect, useMemo, useRef, useState } from 'react';
import type { ParteRisposta } from '@masamune/shared';
import { menoMovimento, pezziDi, unitaDi, type ModoRivela, type Pezzo, type Rivela, type Unita } from './rivela';

/**
 * La risposta di MUNE che compare mentre arriva (29/10/2026, fetta 2B della Fase 2).
 *
 * Prima la risposta arrivava intera e useRivela (rivela.ts) la faceva comparire parola per
 * parola contando il tempo dal suo inizio. In streaming il testo arriva a frasi, e in mezzo
 * possono arrivare riquadri, righe con link e avvisi: contando dal primo pezzo, la seconda
 * frase arrivata un secondo dopo comparirebbe di colpo, tutta insieme.
 *
 * Qui c'è un cursore che avanza a 200 caratteri al secondo (os-planning/ai/fase-2-progetto.md,
 * «Interfaccia») sul testo arrivato, parte dopo parte: le parole sfumano quando il cursore le
 * raggiunge, come prima (stesse unità di rivela.ts, stessa maniera MODO_RIVELA, stesso
 * TestoRivelato); un riquadro o una riga di link compaiono quando il testo prima di loro è
 * tutto comparso. Se il testo arriva più piano del cursore, il cursore aspetta e il tempo
 * d'attesa non si accumula. A fine turno, se resta più di un secondo di testo, il resto
 * compare in un secondo.
 *
 * Il cursore sta fuori dal componente, per id: se il pannello si rimonta a metà (in sviluppo
 * React.StrictMode lo fa subito) riprende da dov'era, senza lampi del testo intero.
 */

/** 200 caratteri al secondo. */
const MS_PER_CARATTERE = 5;
/** A fine turno, quello che resta compare al massimo in un secondo. */
const CODA_MS = 1000;
/**
 * Ogni quanto si muove il cursore: una parola media (6 caratteri) ogni 30 ms, quindi più o
 * meno una parola a passo. Più spesso ridisegnerebbe tutte le parole della risposta per niente.
 */
const OGNI_MS = 24;

interface Cursore {
  /** Fin dove si vede, in caratteri del testo che si legge (senza asterischi), dall'inizio della risposta. */
  pos: number;
  /** Quando si è mosso l'ultima volta (performance.now). */
  at: number;
  /** Millisecondi per carattere: a fine turno può scendere, per finire in un secondo. */
  ms: number;
  /** onFine è già stato chiamato. */
  finito: boolean;
}

const cursori = new Map<string, Cursore>();

function cursoreDi(id: string): Cursore {
  let c = cursori.get(id);
  if (!c) {
    c = { pos: 0, at: performance.now(), ms: MS_PER_CARATTERE, finito: false };
    cursori.set(id, c);
  }
  return c;
}

/** Un testo della risposta, messo in fila: i suoi pezzi, le unità e dove comincia ognuna. */
interface TestoInFila {
  pezzi: Pezzo[];
  piano: string;
  unita: Unita[];
  /** Dove comincia ogni unità, in caratteri dall'inizio della risposta. */
  inizi: number[];
  inizio: number;
}

export interface RivelaFlusso {
  modo: ModoRivela;
  /** Quante parti si vedono: le prime, nell'ordine in cui sono arrivate. */
  partiVisibili: number;
  /** Per ogni parte di testo che si vede (per indice): i pezzi e il suo Rivela, per TestoRivelato. */
  testi: ReadonlyMap<number, { pezzi: Pezzo[]; r: Rivela }>;
  /** La parte di testo dove sta il cursore (la parola che sta comparendo); null se si vede tutto. */
  parteInCorso: number | null;
  /** Quello che è arrivato si vede tutto. */
  inPari: boolean;
  /** C'è testo arrivato che deve ancora comparire. */
  scrive: boolean;
  /** La risposta compare davanti a chi guarda (non era già tutta lì al montaggio): la mascotte salta fuori. */
  entra: boolean;
  /** Mostra subito tutto quello che è arrivato (un clic sulla risposta o sulla mascotte). */
  salta: () => void;
}

/** Quante unità cominciano prima del cursore: quelle che si vedono. */
function unitaFino(inizi: number[], pos: number): number {
  let n = 0;
  while (n < inizi.length && inizi[n]! < pos) n++;
  return n;
}

/** A scatti: quanti caratteri di questo testo si vedono. Mai un'emoji tagliata a metà. */
function caratteriFino(t: TestoInFila, pos: number): number {
  const tot = t.piano.length;
  let n = Math.max(0, Math.min(tot, Math.floor(pos - t.inizio)));
  const c = t.piano.charCodeAt(n - 1);
  if (n < tot && c >= 0xd800 && c <= 0xdbff) n += 1;
  return n;
}

export function useRivelaFlusso(p: {
  id: string;
  parti: ParteRisposta[];
  modo: ModoRivela;
  /** La risposta è appena arrivata (daRivelare): compare man mano. Se no si vede subito tutta. */
  attivo: boolean;
  /** Il flusso è ancora aperto: possono arrivare altre parti. */
  inArrivo: boolean;
  /** Turno finito e tutto comparso. Una volta sola. */
  onFine: () => void;
  /** Allo smontaggio: la risposta non c'è più (nuova conversazione), il suo cursore si dimentica. */
  seSparito?: () => boolean;
}): RivelaFlusso {
  const { id, parti, modo, attivo, inArrivo } = p;

  // Le parti in fila: dove comincia ognuna e, per i testi, le unità. Un riquadro non ha lunghezza.
  const fila = useMemo(() => {
    let pos = 0;
    const inizi: number[] = [];
    const testi = new Map<number, TestoInFila>();
    parti.forEach((parte, i) => {
      inizi.push(pos);
      if (parte.tipo !== 'testo') return;
      const pezzi = pezziDi(parte.testo);
      const piano = pezzi.map((x) => x.testo).join('');
      const unita = modo === 'scatti' ? [] : unitaDi(pezzi, modo);
      const iniziUnita: number[] = [];
      let q = pos;
      for (const u of unita) {
        iniziUnita.push(q);
        q += u.pezzi.reduce((somma, x) => somma + x.testo.length, 0) + u.dopo.length;
      }
      testi.set(i, { pezzi, piano, unita, inizi: iniziUnita, inizio: pos });
      pos += piano.length;
    });
    return { inizi, testi, totale: pos };
  }, [parti, modo]);
  const totale = fila.totale;

  const [pos, setPos] = useState(() => (attivo ? cursoreDi(id).pos : Infinity));
  // Le parole già visibili quando la risposta si è disegnata non sfumano di nuovo.
  const [alMontaggio] = useState(() => (attivo ? cursoreDi(id).pos : Infinity));
  const [entra] = useState(() => attivo);
  const onFine = useRef(p.onFine);
  onFine.current = p.onFine;
  const seSparito = useRef(p.seSparito);
  seSparito.current = p.seSparito;
  const salta = useRef<() => void>(() => {});

  useEffect(() => {
    if (!attivo) return;
    const c = cursoreDi(id);
    const finisci = () => {
      if (inArrivo || c.finito) return;
      c.finito = true;
      onFine.current();
    };
    const vai = (n: number) => { c.pos = n; c.at = performance.now(); setPos(n); };
    salta.current = () => { vai(Math.max(c.pos, totale)); finisci(); };
    if (c.finito || menoMovimento() || c.pos >= totale) {
      vai(Math.max(c.pos, totale));
      finisci();
      return;
    }
    if (!inArrivo) c.ms = Math.min(c.ms, CODA_MS / (totale - c.pos));
    // Il tempo passato ad aspettare il testo non conta: si riparte da adesso.
    c.at = performance.now();
    const t = window.setInterval(() => {
      const ora = performance.now();
      c.pos = Math.min(totale, c.pos + (ora - c.at) / c.ms);
      c.at = ora;
      setPos(c.pos);
      if (c.pos >= totale) {
        window.clearInterval(t);
        finisci();
      }
    }, OGNI_MS);
    return () => window.clearInterval(t);
  }, [attivo, id, totale, inArrivo]);

  // Smontata per davvero (nuova conversazione): il cursore non serve più.
  useEffect(() => () => {
    if (seSparito.current?.()) cursori.delete(id);
  }, [id]);

  const visto = attivo ? pos : Infinity;
  let partiVisibili = 0;
  while (partiVisibili < parti.length && fila.inizi[partiVisibili]! <= visto) partiVisibili++;

  const testi = new Map<number, { pezzi: Pezzo[]; r: Rivela }>();
  let parteInCorso: number | null = null;
  for (const [i, t] of fila.testi) {
    if (i >= partiVisibili) break;
    const aScatti = modo === 'scatti';
    const quante = (x: number) => (aScatti ? caratteriFino(t, x) : unitaFino(t.inizi, x));
    const tot = aScatti ? t.piano.length : t.unita.length;
    const visibili = quante(visto);
    if (visibili < tot) parteInCorso = i;
    testi.set(i, {
      pezzi: t.pezzi,
      r: {
        modo,
        unita: t.unita,
        visibili,
        totale: tot,
        giaAlMontaggio: quante(alMontaggio),
        scrive: visibili < tot,
        salta: () => salta.current(),
      },
    });
  }

  return {
    modo,
    partiVisibili,
    testi,
    parteInCorso,
    inPari: visto >= totale,
    scrive: visto < totale,
    entra,
    salta: () => salta.current(),
  };
}
